import InsightItem from 'components/InsightItem';
import SegmentedBar from 'components/SegmentedBar';
import { useAtomValue } from 'jotai';
import React from 'react';
import { Card } from 'reactstrap';
import { epListingDataAtom } from './state';
import { useEPListFilterValue } from '../hooks';

const EPInsights: React.FC = () => {
  const listingData = useAtomValue(epListingDataAtom);
  const selectedEndpointsFilter = useEPListFilterValue<
    'active' | 'aging' | 'deleted'
  >('filter');
  const insights = listingData?.insights;
  if (!listingData || !insights || Object.keys(insights).length === 0) {
    return <div>Loading...</div>; // Add a loading state
  }
  // Extract stats from insights
  const stats = {
    active: insights.status_breakdown.active,
    aging: insights.status_breakdown.aging,
    deleted: insights.status_breakdown.deleted,
    internal_count: insights.internal_count,
    external_count: insights.external_count,
    aws_count: insights.aws_count,
    total_vulnerabilities: insights.total_vulnerabilities,
    urgent: insights.severity_breakdown.urgent,
    critical: insights.severity_breakdown.critical,
    serious: insights.severity_breakdown.serious,
    medium: insights.severity_breakdown.medium,
    minimal: insights.severity_breakdown.minimal,
    unique_os_count: insights.unique_os_count,
  };

  return (
    <div className="container-fluid border border-light rounded p-0 mb-1">
      <Card
        className="mb-0 px-3 py-1"
        style={{ backgroundColor: 'rgba(34, 42, 66, 0.02)' }}
      >
        <div className="d-flex flex-wrap align-items-center">
          {/* Severity */}
          {selectedEndpointsFilter !== 'deleted' && (
            <div
              className="mr-2 pr-2 flex-grow-1 border-right"
              style={{ borderColor: '#dee2e6' }}
            >
              <h6 className="mb-0">Severity:</h6>
              <SegmentedBar
                height="5px"
                width="100%"
                segments={[
                  {
                    color: 'urgent',
                    value: stats.urgent,
                    label: 'Urgent',
                  },
                  {
                    color: 'critical',
                    value: stats.critical,
                    label: 'Critical',
                  },
                  {
                    color: 'serious',
                    value: stats.serious,
                    label: 'Serious',
                  },
                  {
                    color: 'medium',
                    value: stats.medium,
                    label: 'Medium',
                  },
                  {
                    color: 'minimal',
                    value: stats.minimal,
                    label: 'Minimal',
                  },
                ]}
                labelDisplay="value"
                tooltip={`Urgent: ${stats.urgent}, Critical: ${stats.critical}, Serious: ${stats.serious}, Medium: ${stats.medium}, Minimal: ${stats.minimal}`}
              />
            </div>
          )}

          {/* Count Summaries */}
          {selectedEndpointsFilter !== 'deleted' && (
            <InsightItem
              label="Active"
              value={stats.active}
              tooltip="Active Endpoints, including those nearing aging out."
            />
          )}
          {selectedEndpointsFilter !== 'deleted' && stats.aging > 0 && (
            <InsightItem
              label="Aging Out"
              value={stats.aging}
              tooltip="Endpoints nearing aging out."
            />
          )}
          {selectedEndpointsFilter === 'deleted' && (
            <InsightItem
              label="Deleted"
              value={stats.deleted}
              tooltip="Deleted Endpoints (aged out or manually deleted)."
            />
          )}
          <InsightItem label="Internal" value={stats.internal_count} />
          <InsightItem label="External" value={stats.external_count} />
          <InsightItem label="AWS" value={stats.aws_count} />
          {selectedEndpointsFilter !== 'deleted' && (
            <InsightItem
              label="Vulnerabilities"
              value={stats.total_vulnerabilities}
              tooltip="Total number of vulnerabilities"
              isLast
            />
          )}
        </div>
      </Card>
    </div>
  );
};
export default EPInsights;
